import React, { ElementType, ReactNode } from 'react';
import classNames from 'classnames';
import {
  useViewEvent,
  ViewEventProvider,
  type UseViewEventData,
} from '@stitch-fix/event-reporter';
import { COLORS } from '../_internal/colors';
import Box from '../Box';
import CloudinaryImage, { CloudinaryImageProps } from '../CloudinaryImage';
import Inline from '../Inline';
import Text from '../Text';
import StickerLabel, { StickerLabelVariant } from '../StickerLabel';
import IconYourStyleHangerInactive32 from '../icons/IconYourStyleHangerInactive32';
import styles from './productCard.module.scss';
import { ResponsiveSizeValue, ResponsiveValue } from '../_internal/styling';

type SkuViewEventData = Extract<UseViewEventData, { schema: 'sku_view' }>;

/**
 * If price is undefined, do not allow `fullPrice`
 */
interface NoPriceProps {
  /**
   * Variant price
   */
  price?: undefined;
  /**
   * Variant full price. If provided, ProductCard will render with "Sale" specific styling
   */
  fullPrice?: never;
}

interface PriceOptionalFullPriceProps {
  /**
   * Variant price
   */
  price: string;
  /**
   * Variant full price. If provided, ProductCard will render with "Sale" specific styling
   */
  fullPrice?: string;
}

type PriceProps = NoPriceProps | PriceOptionalFullPriceProps;
type CardSize = ResponsiveValue<'large' | 'small'>;
export interface ProductCardCommonProps {
  /**
   * Container for actions clients can take from the product card
   */
  actionFooter?: ReactNode;
  /**
   * Product brand
   */
  brand?: string;
  /**
   * Size of the product card
   * @default 'large'
   */
  cardSize?: CardSize;
  /**
   * Variant color
   */
  colorName?: string;
  /**
   * Swatches of colors the product is available in.
   * The type determines the placement of the swatches.
   */
  colorTable?: {
    type: 'static' | 'interactive';
    element: ReactNode;
  };
  /**
   * Optional partial event object to override or extend the built-in product tracking event data.
   */
  eventOverrides?: { view: Partial<SkuViewEventData> };
  /**
   * Action presented on top of the hero media using an icon button.
   */
  heroAction?: ReactNode;
  /**
   * Action presented on bottom of the hero media using an icon button.
   */
  heroSecondaryAction?: ReactNode;
  /**
   * Hero media for the product
   */
  heroMedia?: CloudinaryImageProps;
  /**
   * Text displayed when there is no heroMedia
   */
  heroMediaUnavailableText?: string;
  /**
   * The aspect-ratio of the area that holds the key visual for the product
   */
  heroStageAspectRatio?: '4:5' | '1:1';
  /**
   * If true, hides the product details (brand, name, price, size, color) and only displays the hero media.
   */
  hideProductDetails?: boolean;
  /**
   * The URL for the ProductCard link (i.e. PDP)
   */
  href?: string;
  /**
   * Variant inseam
   */
  inseam?: string;
  /**
   * The Link Element type used for all links in the ProductCard component
   * @default 'a';
   */
  linkAs?: 'a' | ElementType;
  /**
   * Max-width of the ProductCard
   */
  maxWidth?: ResponsiveSizeValue;
  /**
   * Variant product name
   */
  name: string;
  /**
   * Callback to execute when a ProductCard is clicked
   */
  onClick?: () => void;
  /**
   * If a variant is out of stock
   */
  outOfStock?: boolean;
  /**
   * Variant size
   */
  size?: string;
  /**
   * Sku ID of the represented product variant
   */
  sku: string;
  /**
   * Used for scarcity and other messaging
   */
  stickerLabel?: {
    text: string;
    variant?: StickerLabelVariant;
  };
}

export type ProductCardProps = ProductCardCommonProps & PriceProps;

const aspectRatioMap = {
  '1:1': 1,
  '4:5': 5 / 4,
};

/** Cloudinary transform that replace shades of predominant or custom colors in an
 * image with shades of a different one with the effect 'replace_color'. */
export const getReplaceColorTransform = (
  hexColor: string,
  threshold = 300,
  targetColor = 'ffffff',
) => {
  const color = hexColor.replace('#', '');

  return `replace_color:${color}:${threshold}:${targetColor}`;
};

/**
 * A ProductCard displays information and actions for a product
 *
 * Exposed via a nested import: `import ProductCard from '@stitch-fix/mode-react/ProductCard';`
 *
 * This component is pre-instrumented with Product Tracking Events (PTEs).
 */
const ProductCard = ({
  actionFooter,
  brand,
  cardSize = 'large',
  colorName,
  colorTable,
  eventOverrides,
  fullPrice,
  heroAction,
  heroSecondaryAction,
  heroMedia,
  heroMediaUnavailableText = 'Image unavailable',
  heroStageAspectRatio = '4:5',
  hideProductDetails = false,
  href,
  inseam,
  linkAs = 'a',
  maxWidth,
  name,
  onClick,
  outOfStock = false,
  price,
  size,
  sku,
  stickerLabel,
}: ProductCardProps) => {
  const nameMaxLines = (() => {
    // Large cards do not get truncated:
    return cardSize === 'small' ? 1 : undefined;
  })();

  const { ref, viewContext, reportSelectEvent } = useViewEvent({
    schema: 'sku_view',
    name: 'sku_card',
    sku_id: sku,
    out_of_stock: outOfStock,
    ...eventOverrides?.view,
  });

  const handleClick = () => {
    reportSelectEvent({
      schema: 'sku_select',
      action_name: 'navigate_to_pdp',
      name: 'sku_card',
      sku_id: sku,
    });

    onClick?.();
  };

  // Formats the size data to display as values are available:
  // size x inseam
  // size
  // inseam
  const displaySizeData = [size, inseam].filter(Boolean).join(' x ');

  const heroMediaDisplay = heroMedia ? (
    <div className={styles['cloudinary-image']}>
      <CloudinaryImage
        {...heroMedia}
        transforms={{
          e: [getReplaceColorTransform(COLORS['gray-95'])],
          ar: heroStageAspectRatio,
        }}
      />
    </div>
  ) : (
    <Box className={styles['image-unavailable']}>
      <IconYourStyleHangerInactive32 color="gray-35" purpose="decorative" />
      <Text mt={0.75} color="gray-35" height="standard" scale="2">
        {heroMediaUnavailableText}
      </Text>
    </Box>
  );

  return (
    <ViewEventProvider viewContext={viewContext}>
      <Box
        maxWidth={maxWidth}
        className={classNames(
          styles.container,
          styles[`container--${cardSize}`],
          {
            [styles['is-out-of-stock']]: outOfStock,
            [styles['is-on-sale']]: fullPrice,
          },
        )}
        ref={ref}
        data-testid="product-card-container"
      >
        <Box>
          {href && (
            <Box
              className={classNames({ [styles.link]: href || onClick })}
              as={href ? linkAs : undefined}
              display="block"
              href={href}
              onClick={handleClick}
              aria-label={[brand, name].filter(Boolean).join(' ')}
            />
          )}
          <Box>
            <Box
              data-testid="hero-media-container"
              className={styles['hero-media-container']}
              pt={`${aspectRatioMap[heroStageAspectRatio] * 100}%`}
            >
              <div className={styles['hero-media']}>{heroMediaDisplay}</div>
              {stickerLabel && (
                <Box className={styles['sticker-label']}>
                  <StickerLabel variant={stickerLabel.variant}>
                    {stickerLabel.text}
                  </StickerLabel>
                </Box>
              )}

              {heroAction && (
                <Box className={styles['hero-action']}>{heroAction}</Box>
              )}

              {heroSecondaryAction && (
                <Box className={styles['hero-secondary-action']}>
                  {heroSecondaryAction}
                </Box>
              )}
            </Box>
            {!hideProductDetails && (
              <Box
                className={classNames(
                  styles['padded-content'],
                  styles['product-details'],
                )}
              >
                {colorTable?.type === 'static' && (
                  <Box className={styles['static-color-swatches-container']}>
                    {colorTable.element}
                  </Box>
                )}
                <Box>
                  <Text maxLines={1} className={styles.brand}>
                    <strong>{brand}</strong>
                  </Text>
                  <Text maxLines={nameMaxLines} className={styles.name}>
                    {name}
                  </Text>
                </Box>
                <Box className={styles['price-container']}>
                  <Inline spacing={0.5}>
                    {price && <Text className={styles.price}>{price}</Text>}
                    {fullPrice && (
                      <Text className={styles['full-price']}>{fullPrice}</Text>
                    )}
                  </Inline>
                </Box>
                {(displaySizeData || colorName) && (
                  <Text
                    as="p"
                    className={styles['size-data']}
                    height="tall"
                    family="regular"
                    scale="2"
                  >
                    {displaySizeData}
                    {/* Display a pipe between size data & color when both exist */}
                    {displaySizeData && colorName && (
                      <Box role="img" aria-hidden as="span" color="gray-75">
                        {` | `}
                      </Box>
                    )}
                    {colorName}
                  </Text>
                )}
              </Box>
            )}
          </Box>
          {colorTable?.type === 'interactive' && (
            <Box
              pt={0.5}
              className={classNames(
                styles['padded-content'],
                styles['interactive-ui'],
              )}
            >
              {colorTable.element}
            </Box>
          )}
        </Box>

        {actionFooter && (
          <Box
            pt={1}
            pb={0.5}
            className={classNames(
              styles['padded-content'],
              styles['interactive-ui'],
            )}
          >
            {actionFooter}
          </Box>
        )}
      </Box>
    </ViewEventProvider>
  );
};

export default ProductCard;
